import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import s from './Step2.module.scss'

import { Creative } from 'types/creative'

import { useAppErrorContext } from 'contexts/AppErrorContext'
import { useData } from 'contexts/CreateCreativeFormContext'
import { useAppSuccessMessageContext } from 'contexts/SuccessMessageContext'

import { creativesApi } from 'api/creatives-api'

import { InfoIcon } from 'assets/icons/InfoIcon'
import { LinkIcon } from 'assets/icons/LinkIcon'
import { TagIcon } from 'assets/icons/TagIcon'

import { InputControlled } from 'components/Controls/Input/InputControlled'
import { TextareaControlled } from 'components/Controls/Textarea/TextareaControlled'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { ModalFullScreen } from 'components/UI/ModalFullScreen/ModalFullScreen'

import { makeCreativeStep2Schema } from 'pages/MyCreatives/validation'

interface FormType {
  name: string
  description: string
  externalLink: string
}

const Step2 = ({ onHide }: { onHide: () => void }) => {
  const [loading, setLoading] = useState(false)
  const { setSuccessMessage } = useAppSuccessMessageContext()
  const { setAppError } = useAppErrorContext()
  const { data, setValues } = useData()

  const { id } = useParams()

  const {
    handleSubmit,
    control,
    formState: { errors },
    setFocus,
    reset,
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(makeCreativeStep2Schema(data?.creativeType)),
    defaultValues: {
      name: '',
      description: '',
      externalLink: '',
    },
  })

  const setDataToForm = async () => {
    if (id) {
      try {
        setLoading(true)
        const { data: creative } = await creativesApi.getOne(+id)
        setLoading(false)
        reset({
          name: creative?.name || '',
          description: creative?.description || '',
          externalLink: creative?.external_link || '',
        })
        setValues({ creativeType: creative.creative_type })
      } catch (e) {
        setAppError(e)
      }
    }
  }

  useEffect(() => {
    setFocus('name')
  }, [])

  useEffect(() => {
    if (id) {
      setDataToForm()
    }
  }, [id])

  const updateCreative = async (fData: FormType) => {
    if (id) {
      setLoading(true)
      try {
        await creativesApi.update(+id, {
          name: fData.name,
          description: fData.description,
          external_link: fData.externalLink || null,
        })
        localStorage.setItem('creativeEdit', 'true')
        setLoading(false)
        setSuccessMessage('The creative successfully edited.')
      } catch (e) {
        setAppError(e)
      }
    }
    onHide()
  }

  const createCreative = async (fData: FormType) => {
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append(
        'data',
        JSON.stringify({
          name: fData.name,
          description: fData.description,
          external_link: fData.externalLink ? fData.externalLink : null,
          stream_url: data.streamUrl ? data.streamUrl : null,
          creative_type: data.creativeType || 0,
          roblox_url: data.robloxUrl || null,
          roblox_video_duration: data.robloxLength || null,
        })
      )
      formData.append('file', data?.files?.[0])
      const res = await creativesApi.create(formData)
      if (res.status === 200) {
        const creative: Creative = res.data
        localStorage.setItem('creativeId', `${creative.id}`)
        onHide()
        setSuccessMessage('The creative successfully created.')
      }
    } catch (error: any) {
      setLoading(false)
      setAppError(error)
    }
    setLoading(false)
  }

  const onSubmit = async (fData: FormType) => {
    setLoading(true)
    if (id) {
      updateCreative(fData)
    } else {
      createCreative(fData)
    }
  }

  return (
    <ModalFullScreen onHide={onHide} loading={loading} className={s.wrapper} testid="step-two-wrapper">
      <h1 className={s.header}>Creative Parameters</h1>
      <form onSubmit={handleSubmit(onSubmit)} className={s.step2wrap} id="createCreativeStep2">
        <div className={s.controlsWrapper}>
          <InputControlled
            icon={<TagIcon />}
            name="name"
            placeholder="Enter name..."
            control={control}
            error={errors?.name?.message}
          />
          <TextareaControlled
            icon={<InfoIcon />}
            control={control}
            name="description"
            placeholder="Describe the creative..."
            height={156}
            error={errors?.description?.message}
          />
          <InputControlled
            icon={<LinkIcon />}
            name="externalLink"
            placeholder={
              data?.creativeType === 10 || data?.creativeType === 11
                ? 'Place ID for teleport'
                : 'External target link...'
            }
            control={control}
            error={errors?.externalLink?.message}
          />
        </div>
      </form>
      <div className={s.buttonsWrapper}>
        <CustomButton testid="submit-button" variant="primary" type="submit" size="240" form="createCreativeStep2" className={s.saveBtn}>
          Save
        </CustomButton>
        <CustomButton variant="outlined" size="152" onClick={onHide}>
          Close
        </CustomButton>
      </div>
    </ModalFullScreen>
  )
}

export default Step2
